import equal from 'fast-deep-equal';

import * as api from '@gitbook/api-client';

import themeBoldDarkImage from './assets/theme-bold-dark.svg';
import themeBoldLightImage from './assets/theme-bold-light.svg';
import themeCleanDarkImage from './assets/theme-clean-dark.svg';
import themeCleanLightImage from './assets/theme-clean-light.svg';
import themeGradientDarkImage from './assets/theme-gradient-dark.svg';
import themeGradientLightImage from './assets/theme-gradient-light.svg';
import themeMutedDarkImage from './assets/theme-muted-dark.svg';
import themeMutedLightImage from './assets/theme-muted-light.svg';
import type { SiteSpaceCustomizationOverrides } from './types';

/**
 * Compares existing and new customization settings
 * and returns a list of the top level fields that have been modified.
 */
export function diffCustomizationSettings(
    initialSettings: api.SiteCustomizationSettings | SiteSpaceCustomizationOverrides,
    settings: SiteSpaceCustomizationOverrides
): Array<string> {
    const modified: Array<string> = [];

    Object.entries(settings).forEach(([key, value]) => {
        if (!Object.hasOwn(initialSettings, key)) {
            modified.push(key);
            return;
        }

        const prevValue = initialSettings[key];
        if (!equal(prevValue, value)) {
            modified.push(key);
        }
    });

    return modified;
}

/**
 * Recursively compares two SiteCustomizationSettings instances
 * and builds an instance of SiteSpaceCustomizationOverrides
 */
export function computeSettingsOverrides(
    initialSettings: api.SiteCustomizationSettings | undefined,
    newSettings: SiteSpaceCustomizationOverrides,
    depth = 0
): SiteSpaceCustomizationOverrides {
    const result: SiteSpaceCustomizationOverrides = {};

    if (!initialSettings) {
        return newSettings;
    }

    for (const key in newSettings) {
        const originalValue = initialSettings[key];
        const overrideValue = newSettings[key];

        // Recurse on first level object attributes
        if (
            overrideValue !== null &&
            typeof overrideValue === 'object' &&
            !Array.isArray(overrideValue) &&
            depth < 1
        ) {
            depth++;

            // Object deep comparison. Like header, footer, etc.
            const subObject = computeSettingsOverrides(originalValue, overrideValue, depth);
            if (Object.keys(subObject).length !== 0) {
                result[key] = subObject;
            }
        } else {
            // Value comparison. Primitives, arrays and our own types.
            if (!equal(overrideValue, originalValue)) {
                result[key] = overrideValue;
            }
        }
    }

    return result;
}

/**
 * Set the themed color for a given theme.
 */
export function setThemedColor(
    input: api.CustomizationThemedColor | undefined | null,
    theme: keyof api.CustomizationThemedColor,
    color: string
): api.CustomizationThemedColor {
    return {
        light: color,
        dark: color,
        ...input,
        [theme]: color,
    };
}

/**
 * Set the themed URL.
 */
export function setThemedURL(
    existing: api.CustomizationThemedURL | undefined | null,
    theme: keyof api.CustomizationThemedURL,
    url: string | null
): api.CustomizationThemedColor | undefined {
    if (existing === null || existing === undefined) {
        if (url === null) {
            return undefined;
        }

        return {
            light: url,
            dark: url,
        };
    }

    // If both light and dark are the same, removing the theme will removes both.
    if (!url) {
        // If both light and dark are the same, we can remove the theme.
        if (existing.dark === existing.light) {
            return undefined;
        }

        // Else we put the other image in the theme slot.
        const other = theme === 'light' ? 'dark' : 'light';
        return { ...existing, [theme]: existing[other] };
    }

    // We update the theme with the new URL.
    return { ...existing, [theme]: url };
}

/**
 * Site themes
 */
export const SITE_THEMES = [
    {
        value: api.CustomizationTheme.Clean,
        label: 'Clean',
        description: 'Modern and minimal',
        needAdvancedCustomization: false,
        asset: {
            light: themeCleanLightImage,
            dark: themeCleanDarkImage,
        },
    },
    {
        value: api.CustomizationTheme.Muted,
        label: 'Muted',
        description: 'Subtle and stylish',
        needAdvancedCustomization: false,
        asset: {
            light: themeMutedLightImage,
            dark: themeMutedDarkImage,
        },
    },
    {
        value: api.CustomizationTheme.Bold,
        label: 'Bold',
        description: 'Strong and confident',
        needAdvancedCustomization: true,
        asset: {
            light: themeBoldLightImage,
            dark: themeBoldDarkImage,
        },
    },
    {
        value: api.CustomizationTheme.Gradient,
        label: 'Gradient',
        description: 'Trendy and colorful',
        needAdvancedCustomization: true,
        asset: {
            light: themeGradientLightImage,
            dark: themeGradientDarkImage,
        },
    },
];
